import { PROJECT_TERMINAL_STATUSES } from '@/lib/constants';

export type EntityType = 'lead' | 'project';

export interface CostLine {
    id: number | string;
    description: string;
    amount: number | string;
    type: string;
    date_incurred: string | Date;
    created_by?:
        | string
        | number
        | {
              id: number | string;
              first_name: string;
              last_name: string;
          };
}

export interface EntityWithCosts {
    id: number | string;
    cost_lines?: CostLine[];
    budget?: number | string; // Deprecated: kept for backward compatibility with leads (estimated_value)
    sales_price?: number | string; // Use for projects
    estimated_value?: number | string; // Use for leads
}

/**
 * Get route path for cost operations
 */
export function getCostRoute(
    entityType: EntityType,
    entityId: number,
    costLineId?: number,
): {
    store: string;
    update: string;
    destroy: string;
} {
    const basePath = `/${entityType}s/${entityId}/cost-lines`;

    return {
        store: `${basePath}`,
        update: `${basePath}/${costLineId || ''}`,
        destroy: `${basePath}/${costLineId || ''}`,
    };
}

/**
 * Get budget value for an entity
 * For projects, uses sales_price
 * For leads, uses estimated_value or budget (for backward compatibility)
 */
export function getEntityBudget(entity: EntityWithCosts): number {
    // Prefer sales_price for projects
    if ('sales_price' in entity && entity.sales_price) {
        return Number(entity.sales_price);
    }
    // Fallback to budget for backward compatibility (leads)
    if (entity.budget) {
        return Number(entity.budget);
    }
    // For leads, use estimated_value
    if ('estimated_value' in entity && entity.estimated_value) {
        return Number(entity.estimated_value);
    }
    return 0;
}

/**
 * Calculate total costs from cost lines
 */
export function calculateTotalCosts(costLines?: CostLine[]): number {
    if (!costLines || costLines.length === 0) return 0;
    return costLines.reduce((sum, cost) => sum + Number(cost.amount || 0), 0);
}

/**
 * Check if costs are locked
 * - Lead costs are locked if the lead has been converted to a project
 * - Project costs are locked if project status is terminal (final status)
 *
 * @param entity - The entity to check (Lead or Project)
 * @param entityType - Entity type to determine which check to perform
 */
export function areCostsLocked(
    entity: EntityWithCosts & {
        project?: { id: number | string } | null;
        status?: string;
    },
    entityType?: EntityType,
): boolean {
    // Use entityType to determine which check to perform
    switch (entityType) {
        case 'project':
            // For projects, only lock if status is terminal
            if (entity.status && PROJECT_TERMINAL_STATUSES.includes(entity.status)) {
                return true;
            }
            return false;

        case 'lead':
            // For leads, lock once converted to a project
            if (entity.project) {
                return true;
            }
            return false;

        default:
            // Fallback: try to infer from entity properties
            // If has status and it's terminal, it's likely a project
            if (entity.status && PROJECT_TERMINAL_STATUSES.includes(entity.status)) {
                return true;
            }
            // If has a project relationship, it is likely a converted lead
            if (entity.project) {
                return true;
            }
            return false;
    }
}
